import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Nav App Controls — approval toggle, commissions flag, rates, payout mins.
 * Approver today: App Admin (= Town Admin). Future: split Town Admin.
 */
const NavAppControls = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/navigator/admin/settings`, {
        headers: authHeaders(),
      });
      setSettings(res.data?.data?.settings || null);
      setRules(res.data?.data?.rules || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Nav settings');
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/navigator/admin/settings`,
        {
          approvalMandatory: settings.approvalMandatory,
          commissionsEnabled: settings.commissionsEnabled,
          payoutOnDemandEnabled: settings.payoutOnDemandEnabled,
          payoutScheduleEnabled: settings.payoutScheduleEnabled,
          payoutMinInr: settings.payoutMinInr,
          merchantRegBaseUrl: settings.merchantRegBaseUrl,
          citizenRegBaseUrl: settings.citizenRegBaseUrl,
          legalBlurb: settings.legalBlurb,
        },
        { headers: authHeaders() },
      );
      setSettings(res.data?.data?.settings || settings);
      toast.success('Nav settings saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveRule = async (rule) => {
    try {
      await axios.put(
        `${API_BASE_URL}/navigator/admin/rules/${rule.id}`,
        { ratePercent: rule.ratePercent, isActive: rule.isActive },
        { headers: authHeaders() },
      );
      toast.success(`Updated ${rule.sourceType}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Rule update failed');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/admin-control-screens"
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Hub
          </Button>
          <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
            Nav App Controls
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              component={RouterLink}
              to="/content/navigators"
            >
              Open approvals
            </Button>
          }
        >
          Approvals are handled by <strong>App Admin</strong> (acts as Town Admin for now)
          on <strong>Loksy Navigators Approval</strong>. Commissions stay OFF until you
          enable them. Payouts go to the Navigator&apos;s registered phone via UPI.
        </Alert>

        {loading || !settings ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Access & commissions
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.approvalMandatory}
                    onChange={(e) =>
                      setSettings({ ...settings, approvalMandatory: e.target.checked })
                    }
                  />
                }
                label="Approval mandatory (new Navigators wait for App Admin)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.commissionsEnabled}
                    onChange={(e) =>
                      setSettings({ ...settings, commissionsEnabled: e.target.checked })
                    }
                  />
                }
                label="Commissions enabled (create credits on paid success)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.payoutOnDemandEnabled}
                    onChange={(e) =>
                      setSettings({ ...settings, payoutOnDemandEnabled: e.target.checked })
                    }
                  />
                }
                label="On-demand payout requests"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.payoutScheduleEnabled}
                    onChange={(e) =>
                      setSettings({ ...settings, payoutScheduleEnabled: e.target.checked })
                    }
                  />
                }
                label="Scheduled payouts (admin batch)"
              />
              <TextField
                label="Minimum payout (INR)"
                type="number"
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                value={settings.payoutMinInr ?? '100'}
                onChange={(e) =>
                  setSettings({ ...settings, payoutMinInr: e.target.value })
                }
              />
              <TextField
                label="Merchant registration base URL"
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                helperText="nav_ref will be appended automatically"
                value={settings.merchantRegBaseUrl || ''}
                onChange={(e) =>
                  setSettings({ ...settings, merchantRegBaseUrl: e.target.value })
                }
              />
              <TextField
                label="Citizen registration base URL"
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                value={settings.citizenRegBaseUrl || ''}
                onChange={(e) =>
                  setSettings({ ...settings, citizenRegBaseUrl: e.target.value })
                }
              />
              <TextField
                label="Legal blurb"
                size="small"
                fullWidth
                multiline
                minRows={3}
                sx={{ mt: 2 }}
                value={settings.legalBlurb || ''}
                onChange={(e) =>
                  setSettings({ ...settings, legalBlurb: e.target.value })
                }
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveSettings}
                  disabled={saving}
                >
                  Save settings
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Default commission rates (%)
              </Typography>
              <Stack spacing={2}>
                {rules.map((rule) => (
                  <Stack
                    key={rule.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                  >
                    <Typography sx={{ minWidth: 140, textTransform: 'capitalize' }}>
                      {String(rule.sourceType).replace('_', ' ')}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="%"
                      value={rule.ratePercent}
                      onChange={(e) =>
                        setRules((prev) =>
                          prev.map((r) =>
                            r.id === rule.id
                              ? { ...r, ratePercent: e.target.value }
                              : r,
                          ),
                        )
                      }
                      sx={{ width: 120 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!rule.isActive}
                          onChange={(e) =>
                            setRules((prev) =>
                              prev.map((r) =>
                                r.id === rule.id
                                  ? { ...r, isActive: e.target.checked }
                                  : r,
                              ),
                            )
                          }
                        />
                      }
                      label="Active"
                    />
                    <Button size="small" onClick={() => saveRule(rule)}>
                      Update
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default NavAppControls;
